/**
 * @typedef {Object} AuthResponse
 * @property {string} token - JWT token
 * @property {string} session - Session ID or key
 */

/**
 * Function to sign in using email and password credentials
 *
 * @param {{ email: string, password: string }} params
 * @returns {Promise<AuthResponse|null>}
 */
export const credentialSignIn = async ({ email, password }) => {
  try {
    const response = await fetch(
      `https://solution.vectolabs.com/api/auth/provider/credentials`,
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.error || "Failed to login");
    }

    /** @type {AuthResponse} */
    const { session, token } = await response.json();
    return { session, token };
  } catch (error) {
    console.error("Login error:", error.message || error);
    return null;
  }
};

/**
 * Function to sign in using email and password credentials
 * token expire every 4hour
 * session is a single use only, will expire after 7 day
 *
 * @param {{ email: string, password: string, service: string }} params
 * @returns {Promise<AuthResponse|null>}
 */
export const credentialSignInService = async ({ email, password, service }) => {
  try {
    const response = await fetch(
      `https://solution.vectolabs.com/api/auth/provider/credentials`,
      {
        method: "POST",
        body: JSON.stringify({ email, password, service }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.error || "Failed to login");
    }

    /** @type {AuthResponse} */
    const { session, token } = await response.json();
    return { session, token };
  } catch (error) {
    console.error("Login error:", error.message || error);
    return null;
  }
};

/**
 *
 * @param {string} token
 * @param {string} service
 * @param {string} session
 * @returns {Promise<AuthResponse|null>}
 */
export const getRefreshToken = async (service, session) => {
  try {
    const response = await fetch(
      `https://solution.vectolabs.com/api/auth/refresh`,
      {
        method: "post",
        body: JSON.stringify({ service, key: session }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to refresh token");
    }
    /** @type {AuthResponse} */
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`refresh token error:`, error.message || error);
    return null;
  }
};

/**
 *
 * @param {string} token
 * @param {string} service
 * @param {string} session
 * @returns {Promise<true|null>}
 */
export const SignOut = async (token, service, session) => {
  try {
    const response = await fetch(
      `https://solution.vectolabs.com/api/auth/revoke`,
      {
        method: "post",
        body: JSON.stringify({ service, key: session }),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 204) return true;

    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to refresh token");
  } catch (error) {
    console.error(`refresh token error:`, error.message || error);
    return null;
  }
};

/**
 * Generate Microsoft OAuth login URL with redirect and service information.
 *
 * @param {string} redirect_success - URL to redirect on successful login
 * @param {string} redirect_error - URL to redirect on error
 * @param {string} service - Service name
 * @returns {string} Fully constructed Microsoft OAuth URL with query parameters
 */
export const MicrosoftOAuthLink = (
  redirect_success,
  redirect_error,
  service
) => {
  const params = new URLSearchParams({
    redirect_success,
    redirect_error,
    service,
  });

  return `https://solution.vectolabs.com/api/auth/provider/microsoft?${params.toString()}`;
//   return `https://79mgxgwl-3000.asse.devtunnels.ms/api/auth/provider/microsoft?${params.toString()}`;
};
